import { useState, useEffect } from 'react';
import { useStudents } from '../../hooks/useStudents';
import { useSubjects } from '../../hooks/useSubjects';
import { useIntakes } from '../../hooks/useIntakes';

export default function EnrollmentForm({ initialData = {}, onSubmit, onCancel, loading = false }) {
  const [formData, setFormData] = useState({
    student_id: '',
    subject_id: '',
    intake_id: '',
    enrollment_date: '',
    status: 'Active'
  });
  
  const [errors, setErrors] = useState({});
  const { students } = useStudents();
  const { subjects } = useSubjects();
  const { intakes } = useIntakes();

  // Initialize form with initial data when component mounts or when initialData.id changes
  useEffect(() => {
    setFormData({
      student_id: initialData.student_id || '',
      subject_id: initialData.subject_id || '',
      intake_id: initialData.intake_id || '',
      enrollment_date: initialData.enrollment_date || '',
      status: initialData.status || 'Active'
    });
    setErrors({});
  }, [initialData.id]);

  const validate = () => {
    const newErrors = {};
    
    if (!formData.student_id) {
      newErrors.student_id = 'Student is required';
    }
    
    if (!formData.subject_id) {
      newErrors.subject_id = 'Subject is required';
    }
    
    if (!formData.intake_id) {
      newErrors.intake_id = 'Intake is required';
    }
    
    if (!formData.enrollment_date) {
      newErrors.enrollment_date = 'Enrollment date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Student *
        </label>
        <select
          name="student_id"
          value={formData.student_id}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md ${
            errors.student_id ? 'border-red-500' : 'border-gray-300'
          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
        >
          <option value="">Select a student</option>
          {students.map(student => (
            <option key={student.id} value={student.id}>
              {student.full_name} ({student.nric_passport})
            </option>
          ))}
        </select>
        {errors.student_id && (
          <p className="mt-1 text-sm text-red-600">{errors.student_id}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Subject *
        </label>
        <select
          name="subject_id"
          value={formData.subject_id}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md ${
            errors.subject_id ? 'border-red-500' : 'border-gray-300'
          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
        >
          <option value="">Select a subject</option>
          {subjects.map(subject => (
            <option key={subject.id} value={subject.id}>
              {subject.code} - {subject.name}
            </option>
          ))}
        </select>
        {errors.subject_id && (
          <p className="mt-1 text-sm text-red-600">{errors.subject_id}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Intake *
        </label>
        <select
          name="intake_id"
          value={formData.intake_id}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md ${
            errors.intake_id ? 'border-red-500' : 'border-gray-300'
          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
        >
          <option value="">Select an intake</option>
          {intakes.map(intake => (
            <option key={intake.id} value={intake.id}>
              {intake.name} ({intake.start_date} to {intake.end_date})
            </option>
          ))}
        </select>
        {errors.intake_id && (
          <p className="mt-1 text-sm text-red-600">{errors.intake_id}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Enrollment Date *
        </label>
        <input
          type="date"
          name="enrollment_date"
          value={formData.enrollment_date}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md ${
            errors.enrollment_date ? 'border-red-500' : 'border-gray-300'
          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
        />
        {errors.enrollment_date && (
          <p className="mt-1 text-sm text-red-600">{errors.enrollment_date}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Status
        </label>
        <select
          name="status"
          value={formData.status}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="Active">Active</option>
          <option value="Completed">Completed</option>
          <option value="Withdrawn">Withdrawn</option>
        </select>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          disabled={loading}
        >
          {loading ? 'Saving...' : (initialData.id ? 'Update Enrollment' : 'Create Enrollment')}
        </button>
      </div>
    </form>
  );
}